import type { SearchSelectOption, SearchSelectPage } from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';

const BASE = '/admin/v1/events';
const PAGE_SIZE = 20;

interface EventAdminViewLite {
  eventId: string;
  eventKey?: string | null;
  title?: string | null;
  description?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  thumbnailUrl?: string | null;
}

interface EventPagePayload {
  content?: EventAdminViewLite[];
  totalElements?: number;
}

// 라벨 정책 (EventSearchSelect 동일): raw eventId 미노출. title 우선 → eventKey → '제목 없음'.
const toOption = (e: EventAdminViewLite): SearchSelectOption => ({
  value: e.eventId,
  label: e.title?.trim() || e.eventKey?.trim() || '제목 없음',
  hint: [e.status, e.startDate, e.endDate].filter(Boolean).join(' · ') || undefined,
  thumbnailUrl: e.thumbnailUrl || null,
});

// BE `/admin/v1/events` 는 keyword param 미지원 → 서버 page 호출 + 클라 필터.
export const fetchEventOptions = async (
  keyword: string,
  page: number,
): Promise<SearchSelectPage> => {
  const qs = new URLSearchParams({
    page: String(page),
    size: String(PAGE_SIZE),
    sort: 'sortOrder,asc',
  });
  const res = await adminFetch(`${BASE}?${qs.toString()}`);
  if (!res.ok) throw new Error(`이벤트 검색 실패 (${res.status})`);
  const env = (await res.json()) as { data?: EventAdminViewLite[] | EventPagePayload };
  const raw = env.data;
  const all = Array.isArray(raw) ? raw : (raw?.content ?? []);
  const totalElements = Array.isArray(raw)
    ? raw.length
    : (raw?.totalElements ?? all.length);

  const trimmed = keyword.trim().toLowerCase();
  const filtered = trimmed
    ? all.filter((e) =>
        [e.title, e.eventKey, e.description ?? '']
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(trimmed),
      )
    : all;

  const items = filtered.map(toOption);
  const hasMore = Array.isArray(raw) ? false : (page + 1) * PAGE_SIZE < totalElements;
  return { items, hasMore, total: totalElements };
};

export const resolveEventOption = async (
  eventId: string,
): Promise<SearchSelectOption | null> => {
  const res = await adminFetch(`${BASE}/${eventId}`);
  if (!res.ok) return null;
  const env = (await res.json()) as { data?: EventAdminViewLite };
  return env.data ? toOption(env.data) : null;
};
